/* eslint-disable no-underscore-dangle */
import { get, set } from 'lodash-es';
import { addDays, parseISO, formatDistanceToNow } from 'date-fns';
import { CookieService } from '@coin-depo-core/storage';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { URL_MAP, UsersApiService } from '@coin-depo/http';
import { LanguageFacade } from '@coin-depo-i18n';

import type {
  ApiResponse,
  ExtendedApiResponse,
  LanguageType,
  LoginRequest,
  LoginApiResponse,
  RegisterApiResponse,
  RegisterRequest,
  ResetPasswordRequest,
  WaitListRequest,
  WaitListResponse,
  ChangePasswordRequest,
  ConfirmationCodeRequest,
  User
} from '@coin-depo-types';

import { environment } from '@coin-depo-config/environments/core-env/environment';

import { UserProfileInfo } from '@coin-depo-core/user-profile-info';
import { StorageService } from '@coin-depo-core/storage';

import { UserTypeService } from '@coin-depo/services/user-type.service';
import { APP_CONFIG, AppConfig } from '@coin-depo-config/tokens';
import { AuthFacade } from '@coin-depo-core/auth/auth.facade';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy, AuthFacade {
  authChanged = new BehaviorSubject<boolean>(false);
  currentUser: UserProfileInfo;
  isRefreshing = false;
  parentId = 'D-122112';

  private destroyed$ = new Subject<void>();
  private broadcastLogout = new BroadcastChannel('cd-logout-channel');

  constructor(
    private router: Router,
    private cookies: CookieService,
    private userApi: UsersApiService,
    private storage: StorageService,
    private userTypeService: UserTypeService,
    private translate: LanguageFacade,
    @Inject(APP_CONFIG) private config: AppConfig
  ) {
    this.currentUser = new UserProfileInfo(this.storage);

    this.checkAuth().then((res) => {
      if (res) {
        this.currentUser[this.isLoggedIn ? 'load' : 'clear']();
      }
    });


    this.currentUser.sLoaded$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res: boolean | null): void => {
        if (res) {
          this.authChanged.next(true);
        }
      });
    this.logoutBroadcastEventInit();
  }

  get showDashboardBanner(): boolean {
    return this.storage.get('showDashboardBanner', true);
  }

  set showDashboardBanner(val: boolean) {
    this.storage.set('showDashboardBanner', val);
  }

  get isLoggedIn(): boolean {
    const data: User = this.userData;
    const token = this.accessToken;
    return !!data && !!token;
  }

  get accessToken(): string {
    return this.cookies.get('accessToken');
  }

  set accessToken(val: string) {
    const expires = addDays(new Date(), 35);

    this.cookies.set('accessToken', val, {
      expires,
      path: '/',
      secure: environment.production,
      sameSite: 'Strict'
    });
  
    this.cookies.set('tokenExpiration', expires.toISOString(), {
      expires,
      secure: environment.production,
      sameSite: 'Strict',
    });
  }

  get userData(): User {
    return this.storage.get('userData') as User;
  }

  set userData(data: Partial<User>) {
    this.storage.set('userData', data);
  }

  get waitListId(): number {
    return this.storage.get('waitListId');
  }

  set waitListId(val: number) {
    this.storage.set('waitListId', val);
  }

  get totpActivated(): boolean {
    return this.storage.get('totpActivated');
  }

  set totpActivated(val: boolean) {
    this.storage.set('totpActivated', val);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  tokenExpiration(left = false): string | Date | null {
    const cookie = this.cookies.get('tokenExpiration');
  
    if (!cookie) return null;
  
    const expiry = parseISO(cookie);
  
    return left
      ? formatDistanceToNow(expiry, { addSuffix: true })
      : expiry;
  }

  verifyTOTP(totp: string, request_id: string) {
    const data = {
      client_token: this.accessToken,
      username: this.userData.username,
      totp,
      browser: window.navigator.userAgent,
      request_id,
    };
    return this.userApi.addTOTP(data);
  }

  delTOTP(totp: string) {
    const data = {
      client_token: this.accessToken,
      username: this.userData.username,
      browser: window.navigator.userAgent,
      totp,
    };
    return this.userApi.delTOTP(data);
  }

  deleteUser(totp: string) {
    const data = {
      client_token: this.accessToken,
      username: this.userData.username,
      totp,
    };
    return this.userApi.deleteUser(data);
  }

  getLastIps(appendData: any = {}) {
    const data = {
      client_token: this.accessToken,
      username: this.userData.username,
      ...appendData,
    };
    return this.userApi.getLastIps(data);
  }

  addTOTP(request_id: string) {
    const data = {
      client_token: this.accessToken,
      username: this.userData.username,
      browser: window.navigator.userAgent,
      request_id,
    };
    return this.userApi.addTOTP(data);
  }

  getUserParam(key: string) {
    const stringified = localStorage.getItem('userParams');
    const userParams = stringified ? JSON.parse(stringified) : {};
    return get(userParams, key, undefined);
  }

  setUserParam(key: string, value: unknown) {
    const stringified = localStorage.getItem('userParams');
    const userParams = stringified ? JSON.parse(stringified) : {};
    set(userParams, key, value);
    localStorage.setItem('userParams', JSON.stringify(userParams));
  }

  sendEmailVerificationCode(email: string, lang: LanguageType) {
    return this.userApi.sendEmailVerificationCode({
      username: email,
      lang,
    });
  }

  login(
    data: Partial<LoginRequest>
  ): Observable<ExtendedApiResponse<LoginApiResponse>> {
    return this.userApi
      .login({
        ...data,
        username: data.email,
        browser: window.navigator.userAgent,
        parent_id: this.parentId,
        no_secure_check: data.no_secure_check ? 1 : 0,
        lang: this.translate.currentLang,
        id: localStorage.getItem('click_id') || '',
        ga_utm: localStorage.getItem('ga_utm') || '',
        subid: localStorage.getItem('subid') || '',
        cdkey: localStorage.getItem('cdkey') || '',
        telegram_id: localStorage.getItem('telegram_id') || '',
      } as LoginRequest)
      .pipe(
        map(
          (
            res: ExtendedApiResponse<LoginApiResponse>
          ): { user: User; token: string } | any => {
            if (res.status === 'success') {
              if (localStorage.getItem('click_id')) {
                localStorage.removeItem('click_id');
              }
              if (localStorage.getItem('ga_utm')) {
                localStorage.removeItem('ga_utm');
              }
              if (localStorage.getItem('subid')) {
                localStorage.removeItem('subid');
              }
              if (localStorage.getItem('cdkey')) {
                localStorage.removeItem('cdkey');
              }
              if (localStorage.getItem('telegram_id')) {
                localStorage.removeItem('telegram_id')
              }

              if (res.code === 'COINDEPO_LISTING') {
                return { downtime: true }
              }

              const email = data.email as string;
              this.userTypeService.setUserType(res.user_type);
              return {
                user: {
                  id: res.user_id,
                  username: email,
                  email,
                },
                token: res.token,
                provider: res.provider,
                action: res.action
              };
            } else {
              return res;
            }
          }
        ),
        catchError((err: HttpErrorResponse) => of(err.error)),
        tap((res: { user: User; token: string } | any) => {
          if (res && typeof res !== 'string' && !res.downtime) {
            this.userData = res.user;
            this.accessToken = res.token;
            this.currentUser.load();
          }
        })
      );
  }

  loginProvider(otpResponse: ExtendedApiResponse<LoginApiResponse>) {
    if (
        otpResponse.user_type &&
        otpResponse.user_id &&
        otpResponse.token &&
        otpResponse.username
    ) {
        this.userTypeService.setUserType(otpResponse.user_type);
        this.userData = {
          id: otpResponse.user_id,
          username: otpResponse.username,
          email: otpResponse.username,
        }, 
        this.accessToken = otpResponse.token;
        this.currentUser.load();
    }
  }

  changePasswordRequest(
    password: string
  ): Observable<ExtendedApiResponse<any>> {
    const data = {
      client_token: this.accessToken,
      password,
    };
    return this.userApi.changePasswordRequest(data).pipe(
      tap(({ status, token }) => {
        if (status === 'success') {
          this.accessToken = token;
        }
      })
    );
  }

  changePasswordConfirmation(
    secret: string,
    new_password: string
  ): Observable<ExtendedApiResponse<any>> {
    const data = {
      client_token: this.accessToken,
      username: this.userData.username,
      secret,
      new_password,
    };
    return this.userApi.changePasswordConfirmation(data);
  }

  logoutBroadcastEventInit() {
    this.broadcastLogout.addEventListener('message', () => {
      this.clear();
      this.currentUser.clear();
      this.authChanged.next(false);
      void this.router.navigate([this.config.loginUrl]);
    });
  }

  logout(redirect = true, backend = true, onLogOut?: () => void): void {
    const clear = () => {
      this.clear();
      this.currentUser.clear();
      this.authChanged.next(false); 
    };
    if (backend) {
      const data = this.userData;
      this.userApi
        .logout({
          username: data.email as string,
          client_token: this.accessToken,
        })
        .subscribe({
          next: (res: ApiResponse) => {
            if (res.status === 'success') {
              onLogOut?.();
              clear();
              if (redirect) {
                void this.router.navigate([this.config.loginUrl]);
              }
            }
          },
        });
    } else {
      onLogOut?.();
      clear();
      if (redirect) {
        void this.router.navigate([this.config.loginUrl]);
      }
    }
    this.broadcastLogout.postMessage({ redirect: true, backend: false });
  }

  logoutBeacon(): void {
    if (!this.accessToken || !this.userData?.email) {
      return;
    }
  
    const params = new URLSearchParams();
    params.append('username', this.userData.email);
    params.append('client_token', this.accessToken);
  
    navigator.sendBeacon(this.getUrl('logout'), params);
  }

  protected getUrl(urlPart: keyof typeof URL_MAP): string {
    const path = get(URL_MAP, urlPart);
    return `${environment.serverUrl}${path}`;
  }

  register(
    data: RegisterRequest
  ): Observable<ExtendedApiResponse<RegisterApiResponse>> {
    return this.userApi.register({
      ...data,
      lang: this.translate.currentLang,
      id: localStorage.getItem('click_id') || '',
      ga_utm: localStorage.getItem('ga_utm') || '',
      subid: localStorage.getItem('subid') || '',
      cdkey: localStorage.getItem('cdkey') || '',
      telegram_id: localStorage.getItem('telegram_id') || '',
    });
  }

  verifyBonusCode(
    data: {['referral']: string}
  ): Observable<ExtendedApiResponse<{retry_after_ms?: number;}>> {
    return this.userApi.verifyBonusCode(data);
  }

  getConfirmationCode(
    data?: ConfirmationCodeRequest
  ): Observable<boolean | ExtendedApiResponse<{retry_after_ms?: number;}>> {
    if (this.userData) {
      const username = this.userData.username || this.userData.email;
      data = data || { client_token: '', username, action: 'register', lang: this.translate.currentLang as LanguageType };
    }

    return this.userApi
      .getConfirmationCode(data as ConfirmationCodeRequest)
      .pipe(
        map((res: ApiResponse): boolean | ExtendedApiResponse<{retry_after_ms?: number;}> => {
          if (res.status === 'success') {
            return true;
          } else {
            return res;
          }
        }),
        catchError((err: HttpErrorResponse) => of(err.error.msg))
      );
  }

  verifyEmail(secret: number, lang: string): Observable<ExtendedApiResponse<{retry_after_ms?: number;}>> {
    const data = this.userData;
    if (data && (data.username || data.email)) {
      return this.userApi.verifyEmail({
        confirmation_code: secret,
        username: data.username || (data.email as string),
        lang,
        id: localStorage.getItem('click_id') || '',
        ga_utm: localStorage.getItem('ga_utm') || '',
        subid: localStorage.getItem('subid') || '',
        cdkey: localStorage.getItem('cdkey') || '',
        telegram_id: localStorage.getItem('telegram_id') || '',
      });
    } else {
      return of({
        status: 'error',
        msg: 'No user data found!',
        code: 'NO_USER_DATA',
      } as ApiResponse);
    }
  }

  resetPassword(data: ResetPasswordRequest): Observable<boolean | ExtendedApiResponse<{retry_after_ms?: number;}>> {
    return this.userApi.resetPassword(data).pipe(
      map((res: ApiResponse): boolean | ExtendedApiResponse<{retry_after_ms?: number;}> => {
        if (res.status === 'error') {
          return res;
        }

        return true;
      }),
      catchError((err: HttpErrorResponse) => of(err.error.msg))
    );
  }

  setPassword(
    newPassword: string,
    code: number,
    check = false,
    lang?: LanguageType
  ): Observable<boolean | ExtendedApiResponse<{retry_after_ms?: number;}>> {
    const data = {
      username: this.userData.email as string,
      password: newPassword,
      secret: code,
      check: check ? 1 : null,
      lang
    };

    return this.userApi.resetPassword(data).pipe(
      map((res: ApiResponse): boolean | ExtendedApiResponse<{retry_after_ms?: number;}> => {
        if (res.status === 'error') {
          return res;
        }

        return true;
      }),
      catchError((err: HttpErrorResponse) => of(err.error.msg))
    );
  }

  changePassword(new_password: string, secret: string) {
    const data: ChangePasswordRequest = {
      username: this.userData.email as string,
      client_token: this.accessToken,
      new_password,
      secret,
      totp: 0,
    };

    return this.userApi.changePassword(data);
  }

  waitListManage(create = false): Observable<number | string> {
    const client_token = this.accessToken;

    if (client_token) {
      const userData = this.currentUser.data$.getValue() as User;

      const data: WaitListRequest = {
        username: userData.username || (userData.email as string),
        client_token,
        status: create ? 1 : 2,
        user_id: userData.id,
      };

      return this.userApi.waitListManage(data).pipe(
        map((res: ExtendedApiResponse<WaitListResponse>): number | string => {
          if (res.status === 'error') {
            return res.msg;
          }

          return res.waitlist_id;
        }),
        catchError((err: HttpErrorResponse) => of(err.error.msg))
      );
    }

    return of('Error while POST to /api/join_waitlist');
  }

  private async checkAuth(): Promise<boolean> {
    // const expire = this.isTokenExpired();
    this.authChanged.next(this.isLoggedIn);
    // if (expire !== null && expire && !this.isRefreshing) {
    //   return await this.reloadAuth();
    // }

    return true;
  }

  private clear(): void {
    this.cookies.deleteAll();
    this.storage.clearAllStorage();
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}
